/**
 * BriefIntakeChat — Phase B2c + chat polish.
 *
 * Multi-turn conversation with the Planning Agent. The agent walks the user
 * through the six gathers and emits the strict approved-brief via a tool call
 * when ready. Owns chat transport + per-message regenerate/edit actions, and
 * surfaces the in-flight `save_brief_plan` tool input upward so the right-
 * rail checklist can stream-render the brief.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { toast } from "sonner";
import { ArrowUp, GripVertical, Pencil, RefreshCw, X, Check, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { composerBus } from "@/lib/composerBus";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import { ChatMarkdown } from "./ChatMarkdown";
import { useCorpusChipResolver } from "./useCorpusChipResolver";

import { PromptInputSubmit } from "@/components/ai-elements/prompt-input";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChatComposerEditor, type ChatComposerEditorHandle, type ChatSegment } from "./ChatComposerEditor";
import { SlashInlinePicker } from "./SlashInlinePicker";
import { UserMessageBody } from "./UserMessageBody";

/**
 * Internal sentinel used to mark a chat message as a template-seed preface
 * (rendered as a distinct card, not a conversational bubble; the Planning
 * Agent reads it as additional context but does not auto-reply to it).
 */
const TEMPLATE_SEED_TAG = "__excerptus_template_seed__";

type TemplateSeedMeta = {
  kind: typeof TEMPLATE_SEED_TAG;
  templateId?: string;
  templateName?: string;
};

const isTemplateSeed = (m: UIMessage): TemplateSeedMeta | null => {
  if (m.role !== "system") return null;
  const meta = (m as UIMessage & { metadata?: unknown }).metadata;
  if (meta && typeof meta === "object" && (meta as TemplateSeedMeta).kind === TEMPLATE_SEED_TAG) {
    return meta as TemplateSeedMeta;
  }
  return null;
};

type LiveBrief = {
  brief_summary?: string | null;
  planning_notes?: string | null;
  approved_brief?: Record<string, unknown> | null;
  intake_checklist?: Record<string, boolean> | null;
} | null;

type MessageMetadataWithSegments = {
  planning_segments?: unknown;
};

type Props = {
  workspaceId: string;
  pieceId: string;
  /**
   * Active planning session id. When provided, the edge function updates
   * this exact `content_brief_plans` row (multi-session support) instead
   * of picking the newest row for the piece.
   */
  planId?: string | null;
  initialMessages: UIMessage[];
  onTurnFinish: () => Promise<void> | void;
  onLiveBriefChange?: (live: LiveBrief) => void;
  /**
   * Called before a destructive turn (regenerate or edit) when the brief
   * has already been approved upstream. Return false to cancel the action.
   */
  onBeforeMutateApproved?: () => Promise<boolean> | boolean;
};

const messageText = (m: UIMessage): string =>
  m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");

const messageSegments = (m: UIMessage): ChatSegment[] | null => {
  const metadata = (m as UIMessage & { metadata?: MessageMetadataWithSegments }).metadata;
  const segments = metadata?.planning_segments;
  return Array.isArray(segments) ? (segments as ChatSegment[]) : null;
};

const buildSegmentsMap = (items: UIMessage[]) => {
  const map = new Map<string, ChatSegment[]>();
  for (const m of items) {
    const segs = messageSegments(m);
    if (segs && segs.length > 0) map.set(m.id, segs);
  }
  return map;
};

/** Extract the latest `save_brief_plan` tool input from a message, if any. */
const extractToolPayload = (m: UIMessage): LiveBrief => {
  for (let i = m.parts.length - 1; i >= 0; i--) {
    const part = m.parts[i] as { type: string; input?: unknown; output?: unknown };
    if (part.type === "tool-save_brief_plan" && part.output && typeof part.output === "object") {
      const output = part.output as { approved_brief?: unknown };
      if (output.approved_brief && typeof output.approved_brief === "object") {
        return { approved_brief: output.approved_brief as Record<string, unknown> };
      }
    }
    if (part.type === "tool-save_brief_plan" && part.input && typeof part.input === "object") {
      return part.input as LiveBrief;
    }
  }
  return null;
};

export const BriefIntakeChat = ({
  workspaceId,
  pieceId,
  planId = null,
  initialMessages,
  onTurnFinish,
  onLiveBriefChange,
  onBeforeMutateApproved,
}: Props) => {
  const editorRef = useRef<ChatComposerEditorHandle | null>(null);
  const focusComposer = () => {
    editorRef.current?.focus();
  };
  const [slashOpen, setSlashOpen] = useState(false);
  const resolveChip = useCorpusChipResolver(workspaceId);

  const [composerEmpty, setComposerEmpty] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [segmentsById, setSegmentsById] = useState<Map<string, ChatSegment[]>>(
    () => buildSegmentsMap(initialMessages),
  );
  const pendingSegmentsRef = useRef<ChatSegment[] | null>(null);
  const pendingRequestSegmentsRef = useRef<ChatSegment[] | null>(null);

  // Resizable input area (drag handle above the input surface).
  const COMPOSER_MIN = 88;
  const COMPOSER_DEFAULT = 160;
  const composerStorageKey = `composer:planningInputHeight:${workspaceId}`;
  const [composerHeight, setComposerHeight] = useState<number>(COMPOSER_DEFAULT);
  const composerDragRef = useRef<{ startY: number; startH: number } | null>(null);
  const composerHeightRef = useRef<number>(COMPOSER_DEFAULT);
  const composerMaxRef = useRef<number>(520);
  useEffect(() => {
    composerHeightRef.current = composerHeight;
  }, [composerHeight]);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(composerStorageKey);
      const parsed = raw ? Number(raw) : NaN;
      if (Number.isFinite(parsed) && parsed >= COMPOSER_MIN) setComposerHeight(parsed);
    } catch {
      /* noop */
    }
  }, [composerStorageKey]);
  useEffect(() => {
    const update = () => {
      composerMaxRef.current = Math.min(window.innerHeight * 0.6, 520);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  const onComposerResizeDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const onWindowPointerMove = (event: PointerEvent) => {
      const drag = composerDragRef.current;
      if (!drag) return;
      const delta = drag.startY - event.clientY;
      const next = Math.min(composerMaxRef.current, Math.max(COMPOSER_MIN, drag.startH + delta));
      composerHeightRef.current = next;
      setComposerHeight(next);
      event.preventDefault();
    };
    const onWindowPointerUp = (event: PointerEvent) => {
      if (!composerDragRef.current) return;
      composerDragRef.current = null;
      window.removeEventListener("pointermove", onWindowPointerMove);
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        window.localStorage.setItem(composerStorageKey, String(composerHeightRef.current));
      } catch {
        /* noop */
      }
      event.preventDefault();
    };
    composerDragRef.current = { startY: e.clientY, startH: composerHeight };
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onWindowPointerMove, { passive: false });
    window.addEventListener("pointerup", onWindowPointerUp, { passive: false });
    window.addEventListener("pointercancel", onWindowPointerUp, { passive: false });
    e.preventDefault();
  }, [COMPOSER_MIN, composerHeight, composerStorageKey]);


  const transport = useMemo(() => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/content-plan-brief`;
    return new DefaultChatTransport({
      api: url,
      fetch: async (input, init) => {
        const { data: { session } } = await supabase.auth.getSession();
        const headers = new Headers(init?.headers);
        if (session?.access_token) {
          headers.set("Authorization", `Bearer ${session.access_token}`);
        }
        headers.set("Content-Type", "application/json");
        let body = init?.body;
        if (typeof body === "string") {
          try {
            const parsed = JSON.parse(body);
            parsed.workspace_id = workspaceId;
            parsed.piece_id = pieceId;
            if (planId) parsed.plan_id = planId;
            if (pendingRequestSegmentsRef.current) {
              parsed.user_segments = pendingRequestSegmentsRef.current;
              pendingRequestSegmentsRef.current = null;
            }
            body = JSON.stringify(parsed);
          } catch {
            // leave as-is
          }
        }
        return fetch(input, { ...init, headers, body });
      },
    });
  }, [workspaceId, pieceId, planId]);

  const { messages, sendMessage, regenerate, setMessages, status, error } = useChat({
    id: planId ?? pieceId,
    messages: initialMessages,
    transport,
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Planning Agent failed";
      toast.error(msg);
    },
    onFinish: () => {
      void onTurnFinish();
      onLiveBriefChange?.(null);
      requestAnimationFrame(() => focusComposer());
    },
  });

  // Surface live tool payload from the in-flight assistant message.
  useEffect(() => {
    if (!onLiveBriefChange) return;
    if (status !== "submitted" && status !== "streaming") {
      onLiveBriefChange(null);
      return;
    }
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "assistant") continue;
      const payload = extractToolPayload(m);
      if (payload) {
        onLiveBriefChange(payload);
        return;
      }
      break;
    }
    onLiveBriefChange(null);
  }, [messages, onLiveBriefChange, status]);

  useEffect(() => {
    requestAnimationFrame(() => focusComposer());
  }, [pieceId]);

  // Sync external transcript → chat state. AI SDK's useChat only reads
  // `initialMessages` once at mount per `id`, so when PlanningRailPanel
  // clears the DB row on Restart and re-passes an empty transcript, we
  // need to explicitly mirror that into the chat's internal state.
  const lastSyncedRef = useRef<UIMessage[] | null>(null);
  useEffect(() => {
    if (lastSyncedRef.current === initialMessages) return;
    lastSyncedRef.current = initialMessages;
    setMessages(initialMessages);
    setSegmentsById(buildSegmentsMap(initialMessages));
  }, [initialMessages, setMessages]);

  // Dynamic-template wizard hand-off: when a Dynamic template's intake is
  // submitted upstream, Composer flips the rail to "planning" mode and emits
  // a `planning:seed` event. We render the seed as a SYSTEM PREFACE message
  // (visible card, role="system") and DO NOT auto-send — the Planning Agent
  // replies on the user's next real turn, having received the seed as
  // additional system context. If the chat already has activity, we stash
  // the seed and offer the user a one-click reset.
  type PendingSeed = {
    text: string;
    source?: string;
    templateId?: string;
    templateName?: string;
  };
  const pendingSeedRef = useRef<PendingSeed | null>(null);

  const buildSeedMessage = useCallback((seed: PendingSeed): UIMessage => {
    const meta: TemplateSeedMeta = {
      kind: TEMPLATE_SEED_TAG,
      templateId: seed.templateId,
      templateName: seed.templateName,
    };
    return {
      id: `seed-${Date.now()}`,
      role: "system",
      parts: [{ type: "text", text: seed.text }],
      metadata: meta,
    } as unknown as UIMessage;
  }, []);

  const applyPendingSeed = useCallback(() => {
    const pending = pendingSeedRef.current;
    if (!pending) return false;
    pendingSeedRef.current = null;
    const seedMsg = buildSeedMessage(pending);
    setMessages([seedMsg]);
    lastSyncedRef.current = [seedMsg];
    return true;
  }, [buildSeedMessage, setMessages]);

  // Drain a pending seed as soon as the chat transitions to empty
  // (e.g. after Restart clears the transcript).
  useEffect(() => {
    if (messages.length === 0 && pendingSeedRef.current) {
      applyPendingSeed();
    }
  }, [messages.length, applyPendingSeed]);

  useEffect(() => {
    return composerBus.subscribe("planning:seed", (payload) => {
      if (payload.pieceId !== pieceId) return;
      const seed: PendingSeed = {
        text: payload.text,
        source: payload.source,
        templateId: payload.templateId,
        templateName: payload.templateName,
      };
      // "Empty for seeding purposes" means no user/assistant turns yet — a
      // prior system seed alone still counts as empty (we can replace it).
      const hasRealTurn = messages.some((m) => m.role === "user" || m.role === "assistant");
      if (!hasRealTurn) {
        pendingSeedRef.current = seed;
        applyPendingSeed();
        return;
      }
      // Conversation already in progress — stash and offer a one-click reset.
      pendingSeedRef.current = seed;
      toast.message("Planning Agent already in progress", {
        description: "Reset the conversation to start this template, or dismiss to keep going.",
        action: {
          label: "Reset and start template",
          onClick: () => {
            // Clearing local messages triggers the drain effect above.
            setMessages([]);
            lastSyncedRef.current = [];
            // Best-effort: also ask the parent to clear server state so the
            // next refresh doesn't re-hydrate the old transcript.
            void supabase
              .from("content_brief_plans")
              .update({
                intake_state: { transcript: [], checklist: null, approved_brief: null },
                brief_summary: null,
                planning_notes: null,
                status: "running",
                error_message: null,
                completed_at: null,
              })
              .eq("brief_id", pieceId)
              .then(() => onTurnFinish());
          },
        },
      });
    });
  }, [pieceId, messages, applyPendingSeed, setMessages, onTurnFinish]);


  const isLoading = status === "submitted" || status === "streaming";

  useEffect(() => {
    const pending = pendingSegmentsRef.current;
    if (!pending) return;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "user") continue;
      if (segmentsById.has(m.id)) return;
      const next = new Map(segmentsById);
      next.set(m.id, pending);
      setSegmentsById(next);
      pendingSegmentsRef.current = null;
      return;
    }
  }, [messages, segmentsById]);

  const submitFromEditor = async () => {
    const editor = editorRef.current;
    if (!editor) return;
    const text = editor.getPlainText().trim();
    if (!text) return;
    const segs = editor.getSegments();
    pendingRequestSegmentsRef.current = segs;
    pendingSegmentsRef.current = segs;
    editor.clear();
    setComposerEmpty(true);
    await sendMessage({ text });
  };

  const handleRegenerate = async () => {
    if (isLoading) return;
    if (onBeforeMutateApproved) {
      const ok = await onBeforeMutateApproved();
      if (!ok) return;
    }
    await regenerate();
  };

  const startEdit = (m: UIMessage) => {
    if (isLoading) return;
    setEditingId(m.id);
    setEditingText(messageText(m));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const submitEdit = async (m: UIMessage) => {
    const next = editingText.trim();
    if (!next) return;
    if (onBeforeMutateApproved) {
      const ok = await onBeforeMutateApproved();
      if (!ok) return;
    }
    const idx = messages.findIndex((x) => x.id === m.id);
    if (idx < 0) return;
    // Truncate up to (but not including) this user message, then re-send the
    // edited text. AI SDK will regenerate forward from this point.
    setMessages(messages.slice(0, idx));
    pendingRequestSegmentsRef.current = null;
    pendingSegmentsRef.current = null;
    setEditingId(null);
    setEditingText("");
    await sendMessage({ text: next });
  };

  // Identify the most recent assistant message id for the regenerate button.
  const lastAssistantId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return messages[i].id;
    }
    return null;
  }, [messages]);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-md border border-border bg-background">
      <Conversation className="min-h-0 flex-1">
        <ConversationContent>
          {messages.map((m) => {
              const seedMeta = isTemplateSeed(m);
              const text = messageText(m);
              if (seedMeta) {
                return (
                  <div
                    key={m.id}
                    className="mx-auto my-2 w-full max-w-2xl rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs"
                  >
                    <div className="mb-1 flex items-center gap-1.5 font-medium text-primary">
                      <Sparkles className="h-3.5 w-3.5" />
                      Seeded from template
                      {seedMeta.templateName ? <span>: {seedMeta.templateName}</span> : null}
                    </div>
                    <p className="whitespace-pre-wrap text-muted-foreground">{text}</p>
                  </div>
                );
              }
              if (!text && m.role !== "assistant") return null;
              const isEditing = editingId === m.id;
              const isUser = m.role === "user";
              const isLastAssistant = !isUser && m.id === lastAssistantId;
              return (
                <div key={m.id} className="group/msg relative">
                  <Message from={isUser ? "user" : "assistant"}>
                    <MessageContent>
                      {isEditing ? (
                        <div className="flex flex-col gap-2 min-w-[260px]">
                          <Textarea
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            rows={3}
                            autoFocus
                            className="text-sm"
                          />
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={cancelEdit}>
                              <X className="h-3.5 w-3.5 mr-1" /> Cancel
                            </Button>
                            <Button size="sm" onClick={() => submitEdit(m)} disabled={!editingText.trim() || isLoading}>
                              <Check className="h-3.5 w-3.5 mr-1" /> Send
                            </Button>
                          </div>
                        </div>
                      ) : isUser ? (
                        <UserMessageBody
                          segments={segmentsById.get(m.id) ?? messageSegments(m)}
                          fallbackText={text}
                        />
                      ) : (
                        <ChatMarkdown resolveChip={resolveChip}>{text}</ChatMarkdown>
                      )}
                    </MessageContent>
                    {!isEditing && (isUser || isLastAssistant) ? (
                      <div className="flex items-center gap-1 opacity-0 group-hover/msg:opacity-100 transition-opacity">
                        {isUser ? (
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            onClick={() => startEdit(m)}
                            disabled={isLoading}
                            title="Edit and re-run from here"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            <span className="sr-only">Edit message</span>
                          </Button>
                        ) : null}
                        {isLastAssistant ? (
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            onClick={handleRegenerate}
                            disabled={isLoading}
                            title="Regenerate this reply"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            <span className="sr-only">Regenerate</span>
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </Message>
                </div>
              );
            })}
          {status === "submitted" ? (
            <Message from="assistant">
              <MessageContent>
                <Shimmer>Thinking…</Shimmer>
              </MessageContent>
            </Message>
          ) : null}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <div className="relative border-t border-border p-3">
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize planning input"
          onPointerDown={onComposerResizeDown}
          className="absolute left-0 right-0 top-0 z-10 flex h-5 -translate-y-1/2 cursor-ns-resize select-none items-center justify-center"
          title="Drag to resize the planning input"
        >
          <div className="z-10 flex h-4 w-3 rotate-90 items-center justify-center rounded-sm border bg-border">
            <GripVertical className="h-2.5 w-2.5" />
          </div>

        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isLoading) return;
            void submitFromEditor();
          }}
          className="flex flex-col gap-2"
        >
          <div className="relative">
            <ChatComposerEditor
              ref={editorRef}
              placeholder={
                messages.length === 0
                  ? "Tell me what you want to plan — I'll walk you through six gathers, one question at a time. Type / to reference a Corpus doc."
                  : messages.every((m) => isTemplateSeed(m))
                    ? "Say hi (or add anything) to start — the Planning Agent has your template context."
                    : "Reply to the Planning Agent… (type / to reference a Corpus doc)"
              }
              disabled={isLoading}
              minHeight={composerHeight}
              onSubmit={() => {
                if (isLoading) return;
                void submitFromEditor();
              }}
              onSlashTrigger={() => setSlashOpen(true)}
              onChange={(isEmpty) => setComposerEmpty(isEmpty)}
            />
            <div className="absolute left-2 top-2">
              <SlashInlinePicker
                workspaceId={workspaceId}
                open={slashOpen}
                onOpenChange={setSlashOpen}
                disabled={isLoading}
                editorRef={editorRef}
              />
            </div>
          </div>
          <div className="flex items-center justify-end">
            <PromptInputSubmit status={status} disabled={isLoading || composerEmpty}>
              <ArrowUp />
            </PromptInputSubmit>
          </div>
        </form>
        {error ? <p className="mt-2 text-xs text-destructive">{error.message}</p> : null}
      </div>
    </div>
  );
};

export default BriefIntakeChat;
